import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  TextField,
  Paper,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DashboardWidgets from "./DashboardWidgets";
import { formatCurrentDate } from "../../shared/helper";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import ConfirmDialog from "../../shared/ConfirmDialog/ConfirmDialog";
import api from "../../services/api";
import PatientSessionModal from "../PatientManagement/PatientSessionsModal";
import AttendanceWidget from "../widgets/AttendanceWidget";

// ---- shared palette (matches PatientTable.js) ---------------------------
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
};

export default function Dashboard({ todayAppointments = [], userDetail }) {
  const [open, setOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [presentPatient, setPresentPatient] = useState(false);
  const [absentPatient, setAbsentPatient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState();
  const [sessionsToday, setSessionsToday] = useState(todayAppointments);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [tempDetails, setTempDetails] = useState({
    location: "",
    temp: "",
    today_appointments: 0,
    active_patients: 0,
    new_patients: 0,
    total_patients: 0,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [noteText, setNoteText] = useState("");

  const branchopen = Boolean(anchorEl);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const DEFAULT_IMAGE =
    BASE_URL + "/static/profile_pic/default_profile_pic.png";

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);
  const handleSnackClose = () =>
    setSnackbarOpen({ status: false, message: "", severity: "" });

  useEffect(() => {
    if (userDetail?.branches?.length && !selectedBranch) {
      setSelectedBranch(userDetail.branches[0]);
    }
  }, [userDetail, selectedBranch]);

  useEffect(() => {
    if (selectedBranch) {
      getPatientStats();
      getTodaySessions();
    }
  }, [selectedBranch]);

  const getPatientStats = async () => {
    try {
      const response = await api.get("app/dashboard/stats", {
        params: { branch_id: selectedBranch.id },
      });

      setTempDetails((prev) => ({
        ...prev,
        today_appointments: response.data.todays_appointments,
        active_patients: response.data.active_patients,
        new_patients: response.data.new_patients,
        total_patients: response.data.total_patients,
      }));
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const getTodaySessions = async () => {
    if (!selectedBranch) return;

    setSessionsLoading(true);

    try {
      const response = await api.get(
        "app/patient/therapy/sessions/today",
        { params: { branch_id: selectedBranch.id } },
      );

      setSessionsToday(response?.data ?? []);
    } catch (error) {
      console.error("Error fetching today's sessions:", error);

      setSnackbarOpen({
        status: true,
        message:
          error?.response?.data?.detail ||
          "Failed to load today's appointments.",
        severity: "error",
      });
    } finally {
      setSessionsLoading(false);
    }
  };

  const getCurrentTemp = async () => {
    try {
      const response = await api.get(
        "http://api.weatherapi.com/v1/current.json",
        {
          params: {
            key: "fa9943c1cc2148baec101942251206",
            q: "Bhubaneswar",
          },
        },
      );

      setTempDetails((prev) => ({
        ...prev,
        location: response.data.location.name,
        temp: response.data.current.temp_c,
      }));
    } catch (error) {
      console.error("Error fetching weather:", error);
    }
  };

  useEffect(() => {
    getCurrentTemp();
  }, []);

  const handlePatientPresent = (sessionId) => {
    setSelectedSessionId(sessionId);
    setPresentPatient(true);
    setAbsentPatient(false);
    setNoteText("");
    handleOpenDialog();
  };

  const handlePatientAbsent = (sessionId) => {
    setSelectedSessionId(sessionId);
    setPresentPatient(false);
    setAbsentPatient(true);
    setNoteText("");
    handleOpenDialog();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (branch) => {
    if (branch) {
      setSelectedBranch(branch);
    }
    setAnchorEl(null);
  };

  const handleAgreePresent = async () => {
    if (!selectedSessionId) return;

    if (!presentPatient && !noteText.trim()) {
      setSnackbarOpen({
        status: true,
        message: "Please provide a reason for absence.",
        severity: "error",
      });
      return;
    }

    const statusPayload = {
      status: presentPatient ? "PRESENT" : "ABSENT",
      notes: presentPatient ? "Present" : noteText,
    };

    try {
      const response = await api.patch(
        `/app/patient/therapy/sessions/${selectedSessionId}/attendance/`,
        statusPayload,
      );

      if (response.status === 200) {
        setSnackbarOpen({
          status: true,
          message: presentPatient
            ? "Patient marked as Present!"
            : "Patient marked as Absent!",
          severity: "success",
        });

        // Refresh today's appointments and stats in place,
        // no full page reload needed.
        if (selectedBranch) {
          getTodaySessions();
          getPatientStats();
        }
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: error?.response?.data?.detail || "An error occurred",
        severity: "error",
      });
    } finally {
      handleCloseDialog();
      setSelectedSessionId(null);
    }
  };

  const handleNameClick = (row) => {
    setSelectedPatient({ id: row.patient_id, patient_name: row.patient_name });
    setOpenPatientModal(true);
  };

  const handleClosePatientModal = () => {
    setOpenPatientModal(false);
    setSelectedPatient(undefined);
  };

  const columns = [
    {
      field: "profile_pic_path",
      headerName: "",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => {
        const imagePath = params.row.profile_pic_path
          ? `${BASE_URL}${params.row.profile_pic_path}`
          : DEFAULT_IMAGE;

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                padding: "2px",
                border: `2px solid ${palette.tealSoft}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={imagePath}
                alt="Profile"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_IMAGE;
                }}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: "patient_name",
      headerName: "Patient Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span
          style={{
            cursor: "pointer",
            fontWeight: 600,
            color: palette.tealDark,
          }}
          onClick={() => handleNameClick(params.row)}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "service_name",
      headerName: "Service",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "session_number",
      headerName: "Session",
      flex: 0.7,
      minWidth: 90,
    },
    {
      field: "start_time",
      headerName: "Start Time",
      flex: 1,
      minWidth: 110,
    },
    {
      field: "end_time",
      headerName: "End Time",
      flex: 1,
      minWidth: 110,
    },
    {
      field: "session_type",
      headerName: "Type",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "action",
      headerName: "Attendance",
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => (
        <AttendanceWidget
          status={params.row.status}
          notes={params.row.notes}
          sessionId={params.row.session_id}
          onPresent={handlePatientPresent}
          onAbsent={handlePatientAbsent}
        />
      ),
    },
  ];

  const filteredRows = sessionsToday.filter((row) =>
    Object.values(row).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  );

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={8}>
            <h1 className="dashboard_heading">Welcome {userDetail?.name}</h1>

            <h5 className="dashboard_subheading">
              Hope you are doing well! Happy to see you again
            </h5>
          </Grid>

          <Grid size={4}>
            <Box className="dashbaord_dateBox">
              <Button
                aria-controls={branchopen ? "branch-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={branchopen ? "true" : undefined}
                onClick={handleClick}
                className="date_button"
                endIcon={<ExpandMoreIcon />}
              >
                {selectedBranch?.name || "Select Branch"}
              </Button>

              <Menu
                id="branch-menu"
                anchorEl={anchorEl}
                open={branchopen}
                onClose={() => handleClose(null)}
              >
                {userDetail?.branches?.map((branch) => (
                  <MenuItem key={branch.id} onClick={() => handleClose(branch)}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <DashboardWidgets
        tempDetails={tempDetails}
        currentDate={formatCurrentDate()}
      />

      <Box className="tabcontent_box" sx={{ backgroundColor: palette.bg }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            padding: "16px 20px",
            marginBottom: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Grid
            container
            spacing={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid size={8}>
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 700,
                  color: palette.ink,
                }}
              >
                Today's Appointments
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: "12.5px",
                  color: palette.slate,
                }}
              >
                {filteredRows?.length ?? 0} appointment
                {filteredRows?.length === 1 ? "" : "s"} scheduled for today.
                Use the action buttons to record attendance.
              </p>
            </Grid>
            <Grid size={4} sx={{ textAlign: "right" }}>
              <TextField
                size="small"
                placeholder="Search appointments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: palette.bg,
                    fontSize: "13.5px",
                    "& fieldset": { borderColor: palette.line },
                    "&:hover fieldset": { borderColor: palette.teal },
                    "&.Mui-focused fieldset": { borderColor: palette.teal },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon
                        sx={{ fontSize: "18px", color: palette.slate }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            overflow: "hidden",
          }}
        >
          <DataGrid
            sx={{
              fontFamily: "Nunito, sans-serif",
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: palette.bg,
                borderBottom: `1px solid ${palette.line}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "12.5px",
                fontWeight: 700,
                color: palette.slate,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "13px",
                borderBottom: `1px solid ${palette.line}`,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: palette.tealSoft,
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${palette.line}`,
              },
            }}
            rows={filteredRows}
            loading={sessionsLoading}
            getRowId={(row) => row.session_id}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
          />
        </Paper>
      </Box>

      <PatientSessionModal
        open={openPatientModal}
        onClose={handleClosePatientModal}
        selectedPatient={selectedPatient}
      />

      <ConfirmDialog
        open={open}
        title={presentPatient ? "Confirm Present" : "Confirm Absent"}
        description={
          presentPatient
            ? "Are you sure you want to mark the Patient as Present? This action cannot be undone."
            : "Are you sure you want to mark the Patient as Absent? This action cannot be undone."
        }
        presentPatient={presentPatient}
        absentPatient={absentPatient}
        agreeText="Yes"
        disagreeText="Cancel"
        onClose={handleCloseDialog}
        onAgree={handleAgreePresent}
        noteText={noteText}
        setNoteText={setNoteText}
      />

      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
    </>
  );
}